import Decimal from 'decimal.js'
import { getMinorUnits } from './currencies'
import type { RatesSnapshot } from './types'

/** Internal precision for cross-rate math; display rounding happens later. */
const Calc = Decimal.clone({ precision: 34, rounding: Decimal.ROUND_HALF_EVEN })

export class MissingRateError extends Error {
  constructor(public readonly code: string) {
    super(`No rate available for ${code}`)
  }
}

function rateOf(snapshot: RatesSnapshot, code: string): Decimal {
  if (code === snapshot.base) return new Calc(1)
  const raw = snapshot.rates[code]
  if (raw === undefined) throw new MissingRateError(code)
  const rate = new Calc(raw)
  if (!rate.isFinite() || rate.lessThanOrEqualTo(0)) throw new MissingRateError(code)
  return rate
}

/** Units of `to` per 1 unit of `from`, derived through the snapshot base. */
export function crossRate(snapshot: RatesSnapshot, from: string, to: string): Decimal {
  if (from === to) return new Calc(1)
  return rateOf(snapshot, to).dividedBy(rateOf(snapshot, from))
}

export interface ConversionResult {
  /** Full-precision converted value. */
  value: Decimal
  /** Value rounded for display in the target currency. */
  display: Decimal
  /** Number of fraction digits used for `display`. */
  fractionDigits: number
  rate: Decimal
}

/**
 * Converts `amount` and picks display precision: the target currency's ISO
 * minor units, unless the value is below 1 and rounding would distort it
 * (e.g. 1 JPY → USD), in which case more decimals are shown.
 */
export function convert(
  snapshot: RatesSnapshot,
  amount: Decimal.Value,
  from: string,
  to: string,
): ConversionResult {
  const rate = crossRate(snapshot, from, to)
  const value = new Calc(amount).times(rate)
  const fractionDigits = displayFractionDigits(value, to)
  const display = value.toDecimalPlaces(fractionDigits, Decimal.ROUND_HALF_UP)
  return { value, display, fractionDigits, rate }
}

/** Max relative error tolerated when rounding sub-unit values for display (0.5%). */
const MAX_DISPLAY_REL_ERROR = new Decimal('0.005')
const MAX_DISPLAY_DECIMALS = 10

export function displayFractionDigits(value: Decimal, currency: string): number {
  const minor = getMinorUnits(currency)
  const abs = value.abs()
  if (abs.isZero() || abs.greaterThanOrEqualTo(1)) return minor
  for (let d = minor; d < MAX_DISPLAY_DECIMALS; d++) {
    const rounded = abs.toDecimalPlaces(d, Decimal.ROUND_HALF_UP)
    if (!rounded.isZero() && rounded.minus(abs).abs().dividedBy(abs).lessThanOrEqualTo(MAX_DISPLAY_REL_ERROR)) {
      return d
    }
  }
  return MAX_DISPLAY_DECIMALS
}

/** Fraction digits for showing a unit rate (e.g. 1 USD = 0.9185 EUR): ≥ 4 significant digits. */
export function rateFractionDigits(rate: Decimal): number {
  const abs = rate.abs()
  if (abs.isZero()) return 2
  if (abs.greaterThanOrEqualTo(1000)) return 2
  if (abs.greaterThanOrEqualTo(1)) return 4
  return Math.min(-abs.e - 1 + 4, 10)
}
